import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dispatch } from "redux";
import { Button, Confirm, TransitionablePortal } from "semantic-ui-react";
import { toast } from "../..";
import { SetCCValidation } from "../../actions/ccValidationActions";
import { SetNPShifts } from "../../actions/npShiftsActions";
import { SetRAOReport } from "../../actions/raoReportActions";
import { SetSelectedTso, SetSelectedTsos } from "../../actions/selectedTsosActions";
import { SetTimestamps } from "../../actions/timestampsActions";
import { SetVertices, SetVerticesMode, SetVertexSelection } from "../../actions/verticesActions";
import { ISavedCalculation, ICalculation } from "../../models/calculation";
import {
    checkSavedCalculation,
    saveCalculation,
    loadSavedCalculation,
} from "../../services/calculationsService";
import { AppState } from "../../store/configureStore";
import { getVerticesString } from "../../store/verticesReducer";
import { IVerticesFiltering, SetSelectedVertexFilteringType, SetVerticesLimits, VertexFilteringType, verticesFilteringInit } from "../../store/verticesFilteringReducer";
import { initialRAOReportState } from "../../store/raoReportReducer";
import { TimestampsStringDict } from "../../store/timestampsReducer";
import { initialCCValidationState } from "../../store/ccValidationReducer";
import { ValidationProcess } from "../../store/validationProcess";
import { ITimestamp, isITimestamp } from "../../components/TimestampsSelector/timestamps";

interface IProps {
    getCalcJson: (calcType?: number) => ICalculation;
}

export const dispatchLoadedCalculation = async (dispatch: Dispatch<any>, calc: ICalculation, availableTimestampsOrScenarios: ITimestamp[]) => {
    /*
    if (calc.validationProcess !== undefined) {
        dispatch(SetValidationProcess(calc.validationProcess, true));
    }

    if (calc.ltccType !== undefined) {
        dispatch(SetLtccType(calc.ltccType));
    }
        */

    if (calc.verticesLimits) {
        let fixedVerticesLimits: IVerticesFiltering[] = [];
        for (let verticesLimitsForHour of calc.verticesLimits) {
            let fixedValues = FixMissingVericesLimits(verticesLimitsForHour);
            fixedVerticesLimits.push(fixedValues);
        }
        calc.verticesLimits = fixedVerticesLimits;
    }

    let activeTimestamps = calc.activeTimestamps ?? [];
    const allItemsAreITimestamps = activeTimestamps.every(item => isITimestamp(item));
    if (allItemsAreITimestamps) {
        const fixedTimestamps = activeTimestamps.filter(item =>
            availableTimestampsOrScenarios.some(totalItem =>
                totalItem.key === item.key &&
                totalItem.value === item.value &&
                totalItem.text === item.text &&
                totalItem.verticesmapkey === item.verticesmapkey &&
                totalItem.cgmtimestamp === item.cgmtimestamp
            )
        );
        activeTimestamps = fixedTimestamps;
    }
    else {
        activeTimestamps = [];
    }
    if (activeTimestamps) {
        dispatch(SetTimestamps(activeTimestamps));
    }
    //dispatch(SetTimestamps(calculationTimestamps));
    if (calc.vertices) {
        dispatch(SetVertices(getVerticesString(calc.vertices, activeTimestamps.map(t => t.key))));
    }
    else {
        dispatch(SetVertices({} as TimestampsStringDict));
    }
    dispatch(SetSelectedVertexFilteringType(calc.selectedVertexFilteringType ?? VertexFilteringType.FilterVertices))
    dispatch(SetCCValidation(calc.ccValidation ?? initialCCValidationState));
    /*
    if (calc.vertexSelection > -1) dispatch(SetVerticesMode("automatic"));
    */
    if (calc.vertexSelection > -1) {
        dispatch(SetVerticesMode("automatic"));
    }
    else {
        dispatch(SetVerticesMode("manual"));
    }
    dispatch(SetVertexSelection(calc.vertexSelection ?? 0));
    dispatch(SetNPShifts(calc.npShifts ?? []));
    dispatch(SetVerticesLimits(calc.verticesLimits ?? []));
    dispatch(SetRAOReport(calc.raoReport ?? initialRAOReportState));
    const tsos = calc.tsos ?? [];
    //set tso only if is defined?
    const selectedTso = tsos.find(item => item !== null && item !== "");
    /*
    if (selectedTso) {

        dispatch(SetSelectedTso(selectedTso));
        dispatch(SetSelectedTso(tsos[0]));
    }
       */
    //dispatch(SetSelectedTso(tsos[0]));
    //dispatch(SetSelectedTso(tsos[0]?? ""));
    dispatch(SetSelectedTso(selectedTso ?? ""));
    dispatch(SetSelectedTsos(calc.tsos ?? []));
};

export function FixMissingVericesLimits(jsonData: Partial<IVerticesFiltering>): IVerticesFiltering {
    const updatedValues = { ...verticesFilteringInit.values };
    for (const key of Object.keys(updatedValues)) {
        const defaultEntry = updatedValues[key as keyof typeof updatedValues];
        const incomingEntry = jsonData.values?.[key];

        if (!incomingEntry) {
            updatedValues[key as keyof typeof updatedValues] = defaultEntry;
        } else {
            const mergedEntry: any = {};
            for (const prop in defaultEntry) {
                if (prop in incomingEntry) {
                    mergedEntry[prop] = incomingEntry[prop];
                } else {
                    mergedEntry[prop] = defaultEntry[prop];
                }
            }
            updatedValues[key as keyof typeof updatedValues] = mergedEntry;
        }
    }

    return {
        hour: jsonData.hour ?? verticesFilteringInit.hour,
        values: updatedValues,
    };
}

export const loadCalcParams = async (
    dispatch: Dispatch<any>, userGuid: string, userTso: string, validationProcess: ValidationProcess, availableTimestampsOrScenarios: ITimestamp[], activeTimestampsRef: ITimestamp[],
) => {
    const res = await loadSavedCalculation(userGuid, validationProcess);
    if (res.status === 200) {
        const savedCalcString = await res.text();
        const savedCalc: ICalculation = JSON.parse(savedCalcString);

        if (!savedCalc.ccValidation?.rangeFromInitial) savedCalc.ccValidation!.rangeFromInitial = false;
        if (!savedCalc.ccValidation?.defaultDownwards) savedCalc.ccValidation!.defaultDownwards = 0;
        if (!savedCalc.ccValidation?.defaultUpwards) savedCalc.ccValidation!.defaultUpwards = 0;
        if (!savedCalc.ccValidation?.scaleByLoad) savedCalc.ccValidation!.scaleByLoad = false;
        if (!savedCalc.ccValidation?.weightDistance) savedCalc.ccValidation!.weightDistance = false;
        /*
        if (savedCalc.verticesLimits) {
            let fixedVerticesLimits: IVerticesFiltering[] = [];
            for (let verticesLimitsForHour of savedCalc.verticesLimits) {
                let fixedValues = FixMissingVericesLimits(verticesLimitsForHour);
                fixedVerticesLimits.push(fixedValues);
            }
            savedCalc.verticesLimits = fixedVerticesLimits;
        }
        */

        //set user tso if list is undefined
        if (Array.isArray(savedCalc.tsos)) {
            savedCalc.tsos = savedCalc.tsos.filter((v) => v != null && v !== "");
        }

        if ((!savedCalc.tsos || savedCalc.tsos.length === 0) && userTso) {
            savedCalc.tsos = [userTso];
        }

        //add user tso if is not contained in the list
        if (userTso && Array.isArray(savedCalc.tsos) && !savedCalc.tsos.includes(userTso)) {
            savedCalc.tsos.unshift(userTso);
        }

        //For LTCC, selected scenarions should be selected, not scenarios that are saved in json
        if (validationProcess === ValidationProcess.Ltcc) {
            savedCalc.activeTimestamps = activeTimestampsRef;
        }

        await dispatchLoadedCalculation(dispatch, savedCalc, availableTimestampsOrScenarios);
        toast("Loaded saved calculation parameters", true);
    } else {
        const savedCalc: ICalculation = JSON.parse("{}");
        //LTCC refresh "fix"
        if (validationProcess === ValidationProcess.Ltcc) {
            savedCalc.activeTimestamps = activeTimestampsRef;
        }
        //set user tso if list is undefined
        if (userTso) {
            savedCalc.tsos = [userTso];
        }
        await dispatchLoadedCalculation(dispatch, savedCalc, availableTimestampsOrScenarios);
        //don't refresh if there is no saved calculations, because there will be an issue with selected scenarios. After the LTCC file is loaded, selected scenarios will be deselected
        toast("No saved calculation parameters found", false);
    }
};

export const SaveLoadCalcButtons = ({ getCalcJson }: IProps) => {
    const dispatch = useDispatch();
    const user = useSelector((state: AppState) => state.user);
    const raoReport = useSelector((state: AppState) => state.raoReport);
    const [loadingLoadingParameters, setLoadingLoadingParameters] = useState(false);
    const [loadingSavingParameters, setLoadingSavingParameters] = useState(false);
    const [saveModalOpen, setSaveModalOpen] = useState(false);
    const validationProcess = useSelector((state: AppState) => state.validationProcess.process);
    const availableTimestampsOrScenarios = useSelector((state: AppState) => state.validationProcess.availableTimestampsOrScenarios);
    const activeTimestamps = useSelector((state: AppState) => state.timestamps.timestamps);

    const handleCheckSavedCalculation = async () => {
        setLoadingSavingParameters(true);
        const res = await checkSavedCalculation(user.guid!, validationProcess);
        if (res.status === 200) setSaveModalOpen(true);
        else await handleSaveCalculation();
    };

    const handleSaveCalculation = async () => {
        setSaveModalOpen(false);
        const calcJson = getCalcJson();
        calcJson.additionalOutage = [];
        calcJson.criticalBranches = [];
        calcJson.f301Necs = [];
        const newSavedCalc: ISavedCalculation = {
            userGuid: user.guid!,
            calculationParameters: JSON.stringify(calcJson),
        };
        const res = await saveCalculation(newSavedCalc, validationProcess);
        if (res.status === 200) {
            toast("Successfully saved calculation parameters", true);
            dispatch(SetRAOReport({ ...raoReport }));
        } else toast("Unable to save calculation parameters", false);
        setLoadingSavingParameters(false);
    };

    const handleLoadCalculation = useCallback(async () => {
        setLoadingLoadingParameters(true);
        await loadCalcParams(dispatch, user.guid!, user.tso!, validationProcess, availableTimestampsOrScenarios, activeTimestamps);
        setLoadingLoadingParameters(false);
    }, [dispatch, user.guid, user.tso, validationProcess, availableTimestampsOrScenarios, activeTimestamps]);

    return (
        <>
            {user.role !== "TSO" && (
                <Button
                    id="button-saveLoadCalc-saveCalculation"
                    content="Save calculation parameters"
                    onClick={handleCheckSavedCalculation}
                    loading={loadingSavingParameters}
                    disabled={loadingSavingParameters}
                />
            )}
            <Button
                id="button-saveLoadCalc-loadCalculation"
                content="Load calculation parameters"
                onClick={handleLoadCalculation}
                loading={loadingLoadingParameters}
                disabled={loadingSavingParameters}
            />
            <TransitionablePortal
                id="transitionablePortal-saveLoadCalc"
                open={saveModalOpen}
                onOpen={() => setTimeout(() => document.body.classList.add("modal-fade-in"), 0)}
                transition={{ animation: "scale", duration: 300 }}
            >
                <Confirm
                    id="confirm-saveLoadCalc"
                    content="This will override your current saved calculation parameters. Are you sure you want to proceed?"
                    open={true}
                    closeOnDimmerClick
                    onCancel={() => {
                        setSaveModalOpen(false);
                        setLoadingSavingParameters(false);
                    }}
                    onConfirm={handleSaveCalculation}
                    size="mini"
                />
            </TransitionablePortal>
        </>
    );
};
